"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, EyeOff, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const guarantees = [{
  id: 1,
  icon: Lock,
  title: "Zero data brokers",
  description: "We never monetize reader data or sell your professional contact details to third-party brokers."
}, {
  id: 2,
  icon: ShieldCheck,
  title: "GDPR & CCPA compliant",
  description: "Full regulatory compliance across every report download, subscription, and research inquiry."
}, {
  id: 3,
  icon: EyeOff,
  title: "Confidential research access",
  description: "Read market analysis, benchmarks, and executive summaries with strict reading privacy."
}];
export default function PrivacyReassurance() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="privacy-data-protection-reassurance" data-nav="light" className="bg-background text-foreground border-t border-border py-14 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Masthead rule detail */}
        <div className="mb-10 sm:mb-12">
          <div className="h-px w-full bg-border" />
          <div className="mt-1 h-px w-24 bg-primary" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left column: Editorial statement & CTA */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col justify-between h-full">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                <span className="inline-block size-1.5 rounded-full bg-accent" />
                <span>Editorial standards</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-foreground leading-snug">
                Reader confidentiality and data protection
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                MinfoMedia operates as an independent publishing institution. We hold your professional information to strict data governance benchmarks, ensuring seamless research access without compromising your privacy.
              </p>
            </div>

            <div className="mt-8 pt-4">
              <Button asChild variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary/5 px-5 py-2.5 h-auto text-sm font-medium transition-[color,background-color,border-color,box-shadow]">
                <Link to="/PrivacyDataProtectionPage" className="inline-flex items-center gap-2">
                  <span>Read our privacy policy</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right column: 3-item reassurance grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {guarantees.map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: shouldReduceMotion ? 0 : i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="bg-card border border-border rounded-md p-5 flex flex-col justify-between shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30">
                  <div>
                    <div className="size-9 rounded-sm bg-muted flex items-center justify-center text-foreground mb-4">
                      <Icon className="size-4.5" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-sans text-sm font-semibold text-foreground tracking-tight mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/60">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      Verified standard
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}